import React, {
  useEffect,
  useRef,
  useMemo,
  useId,
  memo,
  useCallback,
} from "react";
import renderMathInElement from "katex/contrib/auto-render";

// =====================================================================
// GCSE MATHS STANDARDS - Consistent spacing and positioning
// =====================================================================
const GCSE_STANDARDS = {
  // Label positioning distances (in pixels)
  spacing: {
    pointToLabel: 12,           // Distance from point to its label
    lineToLabel: 15,            // Distance from line to its label (perpendicular)
    angleArcRadius: 25,         // Standard angle arc radius
    angleArcToLabel: 12,        // Distance from arc to angle label
    vertexLabelOffset: 12,      // Distance from vertex to label
    sideLabelOffset: 10,        // Distance from side midpoint to label
  },
  
  // Typography
  fontSize: {
    default: 14,
    small: 12,
    large: 16,
    axisLabel: 14,
    tickLabel: 12,
  },
  
  // Colors matching GCSE standards
  colors: {
    primary: "#374151",         // Main elements (dark gray)
    secondary: "#6b7280",       // Secondary elements (medium gray)
    grid: "#e5e7eb",           // Grid lines (light gray)
    point: "#000000",          // Points (black)
    angle: "#000000",          // Angles (black)
    function: "#000000",       // Functions (black)
    shape: "#000000",          // Shapes (black)
    fill: "rgba(59, 130, 246, 0.1)", // Light blue fill
  },
  
  // Standard quadrant positions for labels
  quadrants: {
    topRight: { x: 1, y: -1 },
    topLeft: { x: -1, y: -1 },
    bottomRight: { x: 1, y: 1 },
    bottomLeft: { x: -1, y: 1 },
    top: { x: 0, y: -1 },
    bottom: { x: 0, y: 1 },
    left: { x: -1, y: 0 },
    right: { x: 1, y: 0 },
  },
  
  // Right angle marker size
  rightAngleSize: 12,
  
  // Parallel marks
  parallelMarkSpacing: 8,
  parallelMarkLength: 10,
};

// =====================================================================
// HELPER FUNCTIONS
// =====================================================================

/**
 * Calculate label position based on quadrant
 */
const calculateLabelPosition = (x, y, quadrant = 'topRight', customOffset = null) => {
  if (customOffset) {
    return { x: x + customOffset.x, y: y + customOffset.y };
  }
  
  const offset = GCSE_STANDARDS.quadrants[quadrant] || GCSE_STANDARDS.quadrants.topRight;
  return {
    x: x + offset.x * GCSE_STANDARDS.spacing.pointToLabel,
    y: y + offset.y * GCSE_STANDARDS.spacing.pointToLabel,
  };
};

/**
 * Calculate perpendicular offset for line labels
 */
/**
 * Calculate perpendicular offset for line labels
 */
const calculateLineLabel = (x1, y1, x2, y2, label, side = 'above') => {
  const midX = (x1 + x2) / 2;
  const midY = (y1 + y2) / 2;
  
  // Calculate angle of line
  const angle = Math.atan2(y2 - y1, x2 - x1);
  
  // Check if line is vertical (or nearly vertical)
  const isVertical = Math.abs(x2 - x1) < 0.01;
  
  let labelX, labelY;
  
  if (isVertical) {
    // For vertical lines, place label to the left or right
    const offset = GCSE_STANDARDS.spacing.lineToLabel;
    labelX = midX + (side === 'right' ? offset : -offset);
    labelY = midY;
  } else {
    // For non-vertical lines, use perpendicular offset
    const perpAngle = angle + (side === 'above' || side === 'left' ? -Math.PI / 2 : Math.PI / 2);
    labelX = midX + Math.cos(perpAngle) * GCSE_STANDARDS.spacing.lineToLabel;
    labelY = midY + Math.sin(perpAngle) * GCSE_STANDARDS.spacing.lineToLabel;
  }
  
  // Calculate rotation angle (keep text readable)
  let rotationAngle = (angle * 180) / Math.PI;
  if (rotationAngle > 90 || rotationAngle < -90) {
    rotationAngle += 180;
  }
  
  return { labelX, labelY, rotationAngle, midX, midY };
};

/**
 * Draw right angle marker
 */
const drawRightAngleMarker = (x1, y1, x2, y2, vertexX, vertexY, size = GCSE_STANDARDS.rightAngleSize) => {
  // Calculate direction vectors from vertex to both line endpoints
  const dx1 = x1 - vertexX;
  const dy1 = y1 - vertexY;
  const dx2 = x2 - vertexX;
  const dy2 = y2 - vertexY;
  
  // Normalize to unit vectors
  const len1 = Math.sqrt(dx1 * dx1 + dy1 * dy1);
  const len2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);
  
  const ux1 = (dx1 / len1) * size;
  const uy1 = (dy1 / len1) * size;
  const ux2 = (dx2 / len2) * size;
  const uy2 = (dy2 / len2) * size;
  
  // Three points of the right angle marker
  const p1x = vertexX + ux1;
  const p1y = vertexY + uy1;
  const cornerX = vertexX + ux1 + ux2;
  const cornerY = vertexY + uy1 + uy2;
  const p2x = vertexX + ux2;
  const p2y = vertexY + uy2;
  
  return `M ${p1x} ${p1y} L ${cornerX} ${cornerY} L ${p2x} ${p2y}`;
};

/**
 * Draw parallel marks on a line
 */
const drawParallelMarks = (x1, y1, x2, y2, count = 1) => {
  const midX = (x1 + x2) / 2;
  const midY = (y1 + y2) / 2;
  const angle = Math.atan2(y2 - y1, x2 - x1);
  const perpAngle = angle + Math.PI / 2;
  
  const marks = [];
  const spacing = GCSE_STANDARDS.parallelMarkSpacing;
  const length = GCSE_STANDARDS.parallelMarkLength;
  
  for (let i = 0; i < count; i++) {
    const offset = (i - (count - 1) / 2) * spacing;
    const markX = midX + Math.cos(angle) * offset;
    const markY = midY + Math.sin(angle) * offset;
    
    const x1Mark = markX + Math.cos(perpAngle) * length / 2;
    const y1Mark = markY + Math.sin(perpAngle) * length / 2;
    const x2Mark = markX - Math.cos(perpAngle) * length / 2;
    const y2Mark = markY - Math.sin(perpAngle) * length / 2;
    
    marks.push({ x1: x1Mark, y1: y1Mark, x2: x2Mark, y2: y2Mark });
  }
  
  return marks;
};

/**
 * POLAR COORDINATES FOR ANGLE ARCS
 * 
 * When working with radial UIs (angles, arcs, circles), polar coordinates
 * make everything much simpler and more intuitive than Cartesian coordinates.
 * 
 * Instead of [x, y], we use [distance, angle]:
 * - Distance: How far from the origin
 * - Angle: Direction from reference (North/12 o'clock)
 * 
 * This approach is based on:
 * https://datagraphs.com/blog/graphical-uis-with-svg-and-react-part-2-arcs-angles-and-transformations
 * 
 * Benefits:
 * - Intuitive for radial elements
 * - Avoids edge cases (e.g., angles > 180°)
 * - Simpler arc calculations
 * - Easy to reason about bisector angles
 */

/**
 * Convert polar coordinates to Cartesian coordinates
 * Uses -90° as reference direction (12 o'clock / North)
 * @param distance - Distance from origin
 * @param angleInDegrees - Angle in degrees (0° = North, positive = clockwise)
 * @returns {x, y} Cartesian coordinates
 */
const polarToCartesian = (distance, angleInDegrees) => {
  const REFERENCE_DIRECTION = -90; // Set North as reference
  const angleInRadians = ((REFERENCE_DIRECTION + angleInDegrees) * Math.PI) / 180;
  return {
    x: distance * Math.cos(angleInRadians),
    y: distance * Math.sin(angleInRadians)
  };
};

// =====================================================================
// MAIN COMPONENT
// =====================================================================

const RenderBlocks = ({ blocks }) => {
  if (!blocks || !Array.isArray(blocks)) return null;

  return (
    <div className="render-blocks-container space-y-4">
      {blocks.map((block, index) => (
        <MemoRenderBlock key={index} block={block} />
      ))}
    </div>
  );
};

const RenderBlock = ({ block }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      containerRef.current &&
      (block.type === "text" || block.type === "equation")
    ) {
      try {
        renderMathInElement(containerRef.current, {
          delimiters: [
            { left: "$", right: "$", display: false },
            { left: "$$", right: "$$", display: true },
            { left: "\\(", right: "\\)", display: false },
            { left: "\\[", right: "\\]", display: true },
          ],
          throwOnError: false,
        });
      } catch {
        // no-op; keep rendering even if KaTeX fails
      }
    }
  }, [block.type, block.content, block]);

  switch (block.type) {
    case "text":
      return (
        <div
          ref={containerRef}
          className={`text-block text-gray-700 text-sm sm:text-base ${block.className || ""}`}
          style={block.style}
        >
          {block.content}
        </div>
      );

    case "equation":
      return (
        <div
          ref={containerRef}
          className={`equation-block text-center my-4 text-lg ${block.className || ""}`}
          style={block.style}
        >
          {block.display ? `$$${block.content}$$` : `$${block.content}$`}
        </div>
      );

    case "graph":
      return <MemoGraphRenderer data={block.data} />;

    case "geometry":
      return <MemoGeometryRenderer data={block.data} />;

    case "numberLine":
      return <MemoNumberLineRenderer data={block.data} />;

    case "table":
      return <MemoTableRenderer data={block.data} />;

    case "image":
      return (
        <div
          className={`image-block flex justify-center my-4 ${block.className || ""}`}
          style={block.style}
        >
          <img
            src={block.src}
            alt={block.alt || "Question diagram"}
            className="max-w-full h-auto rounded-lg shadow-md"
            loading="lazy"
            decoding="async"
            onError={(e) => {
              if (!e.target.dataset.fallback) {
                e.target.dataset.fallback = "1";
                e.target.src =
                  block.fallbackSrc ||
                  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='480' height='320'><rect width='100%' height='100%' fill='%23f3f4f6'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='%239ca3af' font-family='sans-serif' font-size='16'>Image unavailable</text></svg>";
              }
            }}
            style={{ maxHeight: block.maxHeight || "400px" }}
          />
        </div>
      );

    case "diagram":
      return <MemoDiagramRenderer data={block.data} />;

    default:
      console.warn(`Unknown block type: ${block.type}`);
      return null;
  }
};

const MemoRenderBlock = memo(RenderBlock);

// =====================================================================
// GRAPH RENDERER with Enhanced Features
// =====================================================================

const GraphRenderer = ({ data = {} }) => {
  const {
    type = "cartesian",
    xRange = [-10, 10],
    yRange = [-10, 10],
    gridSize = 1,
    showGrid = true,
    showAxes = true,
    axesLabels = { x: "x", y: "y" },
    functions = [],
    points = [],
    lines = [],
    shapes = [],
    inequalities = [],
    annotations = [],
    angleArcs = [],
    rightAngleMarkers = [],
    width: svgWidth = 400,
    height: svgHeight = 400,
    padding: svgPadding = 40,
  } = data;

  const arrowId = useId();

  const xScale = useCallback(
    (x) =>
      svgPadding +
      ((x - xRange[0]) / (xRange[1] - xRange[0])) * (svgWidth - 2 * svgPadding),
    [xRange, svgPadding, svgWidth]
  );
  
  const yScale = useCallback(
    (y) =>
      svgHeight -
      svgPadding -
      ((y - yRange[0]) / (yRange[1] - yRange[0])) *
        (svgHeight - 2 * svgPadding),
    [yRange, svgHeight, svgPadding]
  );

  const sanitizeExpression = (expr) => {
    const allowed = /[^0-9+\-*/^()., xsincoqrtaglepmdhMathLOGSQREXPABS]/gi;
    return expr.replace(allowed, "");
  };

  const evaluateExpression = (expr, x) => {
    if (typeof expr === "function") {
      try {
        return expr(x);
      } catch {
        return NaN;
      }
    }

    let expression = sanitizeExpression(String(expr)).replace(/x/g, `(${x})`);

    expression = expression
      .replace(/\^/g, "**")
      .replace(/\bln\b/g, "Math.log")
      .replace(/\blog\b/g, "Math.log")
      .replace(/\bexp\b/g, "Math.exp")
      .replace(/\bsqrt\b/g, "Math.sqrt")
      .replace(/\babs\b/g, "Math.abs")
      .replace(/\bsin\b/g, "Math.sin")
      .replace(/\bcos\b/g, "Math.cos")
      .replace(/\btan\b/g, "Math.tan");

    try {
      // eslint-disable-next-line no-new-func
      return Function(`"use strict"; return (${expression});`)();
    } catch {
      return NaN;
    }
  };

  const generateFunctionPath = (func) => {
    const pts = [];
    const step = (xRange[1] - xRange[0]) / 200;
    for (let x = xRange[0]; x <= xRange[1] + 1e-9; x += step) {
      const y = evaluateExpression(func.expression, x);
      if (isFinite(y) && y >= yRange[0] && y <= yRange[1]) {
        pts.push([xScale(x), yScale(y)]);
      }
    }
    if (!pts.length) return "";
    return pts
      .map((p, i) => (i === 0 ? `M ${p[0]} ${p[1]}` : `L ${p[0]} ${p[1]}`))
      .join(" ");
  };

  const renderInequality = (inequality, iKey) => {
    const {
      expression,
      type,
      fillColor = "rgba(100, 150, 250, 0.2)",
      strokeColor = "rgb(100, 150, 250)",
    } = inequality;

    const pts = [];
    const step = (xRange[1] - xRange[0]) / 100;
    for (let x = xRange[0]; x <= xRange[1] + 1e-9; x += step) {
      const y = evaluateExpression(expression, x);
      if (isFinite(y)) pts.push([xScale(x), yScale(y)]);
    }
    if (!pts.length) return null;

    const basePath = pts
      .map((p, i) => (i === 0 ? `M ${p[0]} ${p[1]}` : `L ${p[0]} ${p[1]}`))
      .join(" ");

    const closingPath =
      type === "above" || type === ">=" || type === ">"
        ? ` L ${xScale(xRange[1])} ${yScale(yRange[0])} L ${xScale(
            xRange[0]
          )} ${yScale(yRange[0])} Z`
        : ` L ${xScale(xRange[1])} ${yScale(yRange[1])} L ${xScale(
            xRange[0]
          )} ${yScale(yRange[1])} Z`;

    return (
      <g key={`ineq-${iKey}`}>
        <path d={basePath + closingPath} fill={fillColor} strokeWidth="0" />
        <path
          d={basePath}
          stroke={strokeColor}
          strokeWidth="2"
          fill="none"
          strokeDasharray={type === ">" || type === "<" ? "5,5" : "0"}
        />
      </g>
    );
  };

  return (
    <div className="graph-container flex justify-center my-4">
      <svg
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        className="w-full h-auto max-w-md border border-gray-200 rounded-lg bg-white shadow-sm"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Graph"
      >
        {/* Grid */}
        {showGrid && (
          <g className="grid">
            {Array.from(
              {
                length: Math.floor((xRange[1] - xRange[0]) / gridSize) + 1,
              },
              (_, i) => {
                const x = xRange[0] + i * gridSize;
                return (
                  <line
                    key={`v-grid-${i}`}
                    x1={xScale(x)}
                    y1={svgPadding}
                    x2={xScale(x)}
                    y2={svgHeight - svgPadding}
                    stroke={GCSE_STANDARDS.colors.grid}
                    strokeWidth="1"
                  />
                );
              }
            )}
            {Array.from(
              {
                length: Math.floor((yRange[1] - yRange[0]) / gridSize) + 1,
              },
              (_, i) => {
                const y = yRange[0] + i * gridSize;
                return (
                  <line
                    key={`h-grid-${i}`}
                    x1={svgPadding}
                    y1={yScale(y)}
                    x2={svgWidth - svgPadding}
                    y2={yScale(y)}
                    stroke={GCSE_STANDARDS.colors.grid}
                    strokeWidth="1"
                  />
                );
              }
            )}
          </g>
        )}

        {/* Axes */}
        {showAxes && (
          <g className="axes">
            <line
              x1={svgPadding}
              y1={yScale(0)}
              x2={svgWidth - svgPadding}
              y2={yScale(0)}
              stroke={GCSE_STANDARDS.colors.primary}
              strokeWidth="2"
            />
            <line
              x1={xScale(0)}
              y1={svgPadding}
              x2={xScale(0)}
              y2={svgHeight - svgPadding}
              stroke={GCSE_STANDARDS.colors.primary}
              strokeWidth="2"
            />
            <polygon
              points={`${svgWidth - svgPadding},${yScale(0)} ${
                svgWidth - svgPadding - 8
              },${yScale(0) - 4} ${svgWidth - svgPadding - 8},${yScale(0) + 4}`}
              fill={GCSE_STANDARDS.colors.primary}
            />
            <polygon
              points={`${xScale(0)},${svgPadding} ${xScale(0) - 4},${
                svgPadding + 8
              } ${xScale(0) + 4},${svgPadding + 8}`}
              fill={GCSE_STANDARDS.colors.primary}
            />
            <text
              x={svgWidth - svgPadding + 5}
              y={yScale(0) - 5}
              fontSize={GCSE_STANDARDS.fontSize.axisLabel}
              fill={GCSE_STANDARDS.colors.primary}
            >
              {axesLabels.x}
            </text>
            <text
              x={xScale(0) + 5}
              y={svgPadding - 5}
              fontSize={GCSE_STANDARDS.fontSize.axisLabel}
              fill={GCSE_STANDARDS.colors.primary}
            >
              {axesLabels.y}
            </text>

            {Array.from(
              {
                length: Math.floor((xRange[1] - xRange[0]) / gridSize) + 1,
              },
              (_, i) => {
                const x = xRange[0] + i * gridSize;
                if (x === 0) return null;
                return (
                  <g key={`x-tick-${i}`}>
                    <line
                      x1={xScale(x)}
                      y1={yScale(0) - 3}
                      x2={xScale(x)}
                      y2={yScale(0) + 3}
                      stroke={GCSE_STANDARDS.colors.primary}
                      strokeWidth="1"
                    />
                    <text
                      x={xScale(x)}
                      y={yScale(0) + 18}
                      fontSize={GCSE_STANDARDS.fontSize.tickLabel}
                      textAnchor="middle"
                      fill={GCSE_STANDARDS.colors.primary}
                    >
                      {x}
                    </text>
                  </g>
                );
              }
            )}
            {Array.from(
              {
                length: Math.floor((yRange[1] - yRange[0]) / gridSize) + 1,
              },
              (_, i) => {
                const y = yRange[0] + i * gridSize;
                if (y === 0) return null;
                return (
                  <g key={`y-tick-${i}`}>
                    <line
                      x1={xScale(0) - 3}
                      y1={yScale(y)}
                      x2={xScale(0) + 3}
                      y2={yScale(y)}
                      stroke={GCSE_STANDARDS.colors.primary}
                      strokeWidth="1"
                    />
                    <text
                      x={xScale(0) - 10}
                      y={yScale(y) + 4}
                      fontSize={GCSE_STANDARDS.fontSize.tickLabel}
                      textAnchor="end"
                      fill={GCSE_STANDARDS.colors.primary}
                    >
                      {y}
                    </text>
                  </g>
                );
              }
            )}
          </g>
        )}

        {/* Inequality regions */}
        {inequalities.map((ineq, i) => renderInequality(ineq, i))}

        {/* Shapes */}
        {shapes.map((shape, i) => {
          const { type, ...props } = shape;
          switch (type) {
            case "circle": {
              if (!props.center || typeof props.radius !== "number") return null;
              const rx =
                (props.radius / (xRange[1] - xRange[0] || 1)) *
                (svgWidth - 2 * svgPadding);
              const ry =
                (props.radius / (yRange[1] - yRange[0] || 1)) *
                (svgHeight - 2 * svgPadding);
              return (
                <ellipse
                  key={`shape-${i}`}
                  cx={xScale(props.center[0])}
                  cy={yScale(props.center[1])}
                  rx={rx}
                  ry={ry}
                  fill={props.fill || "none"}
                  stroke={props.stroke || GCSE_STANDARDS.colors.primary}
                  strokeWidth={props.strokeWidth || "2"}
                  strokeDasharray={props.dashed ? "5,5" : "0"}
                />
              );
            }
            case "rectangle":
              return (
                <rect
                  key={`shape-${i}`}
                  x={xScale(Math.min(props.corner1[0], props.corner2[0]))}
                  y={yScale(Math.max(props.corner1[1], props.corner2[1]))}
                  width={Math.abs(
                    xScale(props.corner2[0]) - xScale(props.corner1[0])
                  )}
                  height={Math.abs(
                    yScale(props.corner2[1]) - yScale(props.corner1[1])
                  )}
                  fill={props.fill || "none"}
                  stroke={props.stroke || GCSE_STANDARDS.colors.primary}
                  strokeWidth={props.strokeWidth || "2"}
                  strokeDasharray={props.dashed ? "5,5" : "0"}
                />
              );
            case "polygon":
              return (
                <polygon
                  key={`shape-${i}`}
                  points={props.vertices
                    .map((v) => `${xScale(v[0])},${yScale(v[1])}`)
                    .join(" ")}
                  fill={props.fill || "none"}
                  stroke={props.stroke || GCSE_STANDARDS.colors.primary}
                  strokeWidth={props.strokeWidth || "2"}
                  strokeDasharray={props.dashed ? "5,5" : "0"}
                />
              );
            default:
              return null;
          }
        })}

        {/* Functions */}
        {functions.map((func, i) => (
          <path
            key={`function-${i}`}
            d={generateFunctionPath(func)}
            stroke={func.color || GCSE_STANDARDS.colors.function}
            strokeWidth={func.strokeWidth || "2"}
            fill="none"
            strokeDasharray={func.dashed ? "5,5" : "0"}
          />
        ))}

        {/* Lines with enhanced labeling */}
        {lines.map((line, i) => {
          const x1 = xScale(line.start[0]);
          const y1 = yScale(line.start[1]);
          const x2 = xScale(line.end[0]);
          const y2 = yScale(line.end[1]);
          
          const labelData = line.label
            ? calculateLineLabel(x1, y1, x2, y2, line.label, line.labelSide || 'above')
            : null;
          
          // Check for right angle marker
          const showRightAngle = line.rightAngle;
          
          // Check for parallel marks
          const parallelMarks = line.parallelMarks 
            ? drawParallelMarks(x1, y1, x2, y2, line.parallelMarks)
            : null;

          return (
            <g key={`line-${i}`}>
              <line
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={line.color || GCSE_STANDARDS.colors.primary}
                strokeWidth={line.strokeWidth || "2"}
                strokeDasharray={line.dashed ? "5,5" : "0"}
                markerEnd={line.arrow ? `url(#arrow-${arrowId})` : ""}
              />
              
              {/* Parallel marks */}
              {parallelMarks && parallelMarks.map((mark, idx) => (
                <line
                  key={`parallel-${i}-${idx}`}
                  x1={mark.x1}
                  y1={mark.y1}
                  x2={mark.x2}
                  y2={mark.y2}
                  stroke={line.color || GCSE_STANDARDS.colors.primary}
                  strokeWidth="2"
                />
              ))}
              
              {/* Right angle marker - needs to know which two lines form the right angle */}
              {showRightAngle && line.rightAngleLines && (
                <path
                  d={drawRightAngleMarker(
                    xScale(line.rightAngleLines.line1.start[0]),
                    yScale(line.rightAngleLines.line1.start[1]),
                    xScale(line.rightAngleLines.line1.end[0]),
                    yScale(line.rightAngleLines.line1.end[1]),
                    xScale(line.rightAngleLines.vertex[0]),
                    yScale(line.rightAngleLines.vertex[1])
                  )}
                  stroke={line.color || GCSE_STANDARDS.colors.primary}
                  strokeWidth="2"
                  fill="none"
                />
              )}
              
              {/* Line label with rotation */}
              {labelData && line.label && (
                <text
                  x={labelData.labelX}
                  y={labelData.labelY}
                  fontSize={GCSE_STANDARDS.fontSize.default}
                  fill={GCSE_STANDARDS.colors.primary}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  transform={line.rotateLabel !== false 
                    ? `rotate(${labelData.rotationAngle} ${labelData.labelX} ${labelData.labelY})`
                    : undefined
                  }
                >
                  {line.label}
                </text>
              )}
            </g>
          );
        })}

        {/* Points with smart positioning */}
        {points.map((point, i) => {
          const px = xScale(point.x);
          const py = yScale(point.y);
          
          const labelPos = point.label
            ? calculateLabelPosition(
                px,
                py,
                point.labelQuadrant || 'topRight',
                point.labelOffset
              )
            : null;

          return (
            <g key={`point-${i}`}>
              <circle
                cx={px}
                cy={py}
                r={point.size || 4}
                fill={point.filled !== false 
                  ? (point.color || GCSE_STANDARDS.colors.point)
                  : "white"
                }
                stroke={point.color || GCSE_STANDARDS.colors.point}
                strokeWidth="2"
              />
              {point.label && labelPos && (
                <text
                  x={labelPos.x}
                  y={labelPos.y}
                  fontSize={GCSE_STANDARDS.fontSize.default}
                  fill={GCSE_STANDARDS.colors.primary}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontWeight="normal"
                >
                  {point.label}
                </text>
              )}
            </g>
          );
        })}

        {/* Annotations */}
        {annotations.map((annotation, i) => (
          <text
            key={`annotation-${i}`}
            x={xScale(annotation.x)}
            y={yScale(annotation.y)}
            fontSize={annotation.fontSize || GCSE_STANDARDS.fontSize.default}
            fill={annotation.color || GCSE_STANDARDS.colors.primary}
            textAnchor={annotation.anchor || "middle"}
          >
            {annotation.text}
          </text>
        ))}
{/* Angle Arcs - Using proper SVG arc commands */}
{angleArcs &&
  angleArcs.map((arc, i) => {
    const centerX = xScale(arc.center[0]);
    const centerY = yScale(arc.center[1]);
    
    // Require points array for accuracy
    if (!arc.points || arc.points.length !== 2) {
      console.warn('Angle arc requires "points" array with 2 endpoints');
      return null;
    }
    
    const [p1, p2] = arc.points;
    
    // Calculate angles from center to each point in SVG coordinates
    const dx1 = xScale(p1[0]) - centerX;
    const dy1 = yScale(p1[1]) - centerY;
    const dx2 = xScale(p2[0]) - centerX;
    const dy2 = yScale(p2[1]) - centerY;
    
    // Get angles in radians
    let angle1 = Math.atan2(dy1, dx1);
    let angle2 = Math.atan2(dy2, dx2);
    
    // Calculate angle difference in radians
    let angleDiff = angle2 - angle1;
    
    // Normalize to -π to π range
    while (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
    while (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;
    
    // Check if user wants to force drawing the smaller interior angle
    // (useful for parallel lines where we always want acute/obtuse, not reflex)
    const forceInterior = arc.forceInterior !== false; // Default true
    
    if (forceInterior && Math.abs(angleDiff) > Math.PI) {
      // Draw the complementary angle instead
      angleDiff = angleDiff > 0 ? angleDiff - 2 * Math.PI : angleDiff + 2 * Math.PI;
    }
    
    const absAngle = Math.abs(angleDiff);
    
    // Larger default radius for better label spacing
    const radius = arc.radius || 40;
    
    // Calculate arc endpoints
    const x1 = centerX + radius * Math.cos(angle1);
    const y1 = centerY + radius * Math.sin(angle1);
    const x2 = centerX + radius * Math.cos(angle2);
    const y2 = centerY + radius * Math.sin(angle2);
    
    // Arc flags
    const largeArcFlag = absAngle > Math.PI ? 1 : 0;
    const sweepFlag = angleDiff > 0 ? 1 : 0;
    
    // Label position - at the bisector
    const midAngle = angle1 + angleDiff / 2;
    const labelRadius = radius * 0.7;
    const labelX = centerX + labelRadius * Math.cos(midAngle);
    const labelY = centerY + labelRadius * Math.sin(midAngle);
    
    const labelText = arc.label || '';

    return (
      <g key={`angle-arc-${i}`}>
        {/* Arc path */}
        <path
          d={`M ${x1},${y1} A ${radius},${radius} 0 ${largeArcFlag},${sweepFlag} ${x2},${y2}`}
          stroke={arc.color || GCSE_STANDARDS.colors.angle}
          strokeWidth="1.5"
          fill="none"
          strokeLinecap="round"
        />
        
        {/* Label */}
        {labelText && (
          <text
            x={labelX}
            y={labelY}
            fontSize="11"
            textAnchor="middle"
            dominantBaseline="middle"
            fill={arc.labelColor || arc.color || GCSE_STANDARDS.colors.angle}
          >
            {labelText}
          </text>
        )}
      </g>
    );
  })}
        {/* Right Angle Markers */}
        {rightAngleMarkers.map((marker, i) => {
          const vertex = marker.vertex;
          const point1 = marker.point1;
          const point2 = marker.point2;
          
          const vx = xScale(vertex[0]);
          const vy = yScale(vertex[1]);
          const p1x = xScale(point1[0]);
          const p1y = yScale(point1[1]);
          const p2x = xScale(point2[0]);
          const p2y = yScale(point2[1]);
          
          return (
            <path
              key={`right-angle-${i}`}
              d={drawRightAngleMarker(p1x, p1y, p2x, p2y, vx, vy, marker.size || GCSE_STANDARDS.rightAngleSize)}
              stroke={marker.color || GCSE_STANDARDS.colors.primary}
              strokeWidth="2"
              fill="none"
            />
          );
        })}

        {/* Arrow marker */}
        <defs>
          <marker
            id={`arrow-${arrowId}`}
            markerWidth="10"
            markerHeight="10"
            refX="8"
            refY="3"
            orient="auto"
          >
            <polygon points="0 0, 10 3, 0 6" fill={GCSE_STANDARDS.colors.primary} />
          </marker>
        </defs>
      </svg>
    </div>
  );
};

const MemoGraphRenderer = memo(GraphRenderer);

// =====================================================================
// GEOMETRY RENDERER with Enhanced Features
// =====================================================================

const GeometryRenderer = ({ data = {} }) => {
  const {
    type = "triangle",
    vertices = [],
    angles = [],
    sides = [],
    labels = [],
    labelPositions = [],
    showMeasurements = false,
    showAngles = false,
    showDiagonals = false,
    transformations = [],
    style = {},
    width: svgWidth = 400,
    height: svgHeight = 400,
    padding: svgPadding = 40,
  } = data;

  const allX = vertices
    .map((v) => v?.[0])
    .filter((x) => typeof x === "number" && !Number.isNaN(x));
  const allY = vertices
    .map((v) => v?.[1])
    .filter((y) => typeof y === "number" && !Number.isNaN(y));
  const minX = allX.length ? Math.min(...allX) : 0;
  const maxX = allX.length ? Math.max(...allX) : 10;
  const minY = allY.length ? Math.min(...allY) : 0;
  const maxY = allY.length ? Math.max(...allY) : 10;

  const scaleX = useCallback(
    (x) => {
      if (typeof x !== "number" || Number.isNaN(x))
        return svgPadding + (svgWidth - 2 * svgPadding) / 2;
      const range = maxX - minX || 1;
      const val = svgPadding + ((x - minX) / range) * (svgWidth - 2 * svgPadding);
      return Number.isNaN(val) ? svgPadding + (svgWidth - 2 * svgPadding) / 2 : val;
    },
    [minX, maxX, svgPadding, svgWidth]
  );

  const scaleY = useCallback(
    (y) => {
      if (typeof y !== "number" || Number.isNaN(y))
        return svgHeight - svgPadding - (svgHeight - 2 * svgPadding) / 2;
      const range = maxY - minY || 1;
      const val =
        svgHeight - svgPadding - ((y - minY) / range) * (svgHeight - 2 * svgPadding);
      return Number.isNaN(val)
        ? svgHeight - svgPadding - (svgHeight - 2 * svgPadding) / 2
        : val;
    },
    [minY, maxY, svgPadding, svgHeight]
  );

  const drawAngleArc = (vertex, prev, next, angle, label) => {
    const vx = scaleX(vertex[0]);
    const vy = scaleY(vertex[1]);
    const px = scaleX(prev[0]);
    const py = scaleY(prev[1]);
    const nx = scaleX(next[0]);
    const ny = scaleY(next[1]);

    const angle1 = Math.atan2(py - vy, px - vx);
    const angle2 = Math.atan2(ny - vy, nx - vx);

    const radius = 45;

    // Calculate angle difference
    let angleDiff = angle2 - angle1;

    // Normalize to -2π to 2π range
    while (angleDiff > 2 * Math.PI) angleDiff -= 2 * Math.PI;
    while (angleDiff < -2 * Math.PI) angleDiff += 2 * Math.PI;

    // Always use the smaller angle (interior angle)
    if (Math.abs(angleDiff) > Math.PI) {
      angleDiff = angleDiff > 0 ? angleDiff - 2 * Math.PI : angleDiff + 2 * Math.PI;
    }

    const largeArcFlag = Math.abs(angleDiff) > Math.PI ? 1 : 0;
    const sweepFlag = angleDiff > 0 ? 1 : 0;
    const x1 = vx + radius * Math.cos(angle1);
    const y1 = vy + radius * Math.sin(angle1);
    const x2 = vx + radius * Math.cos(angle2);
    const y2 = vy + radius * Math.sin(angle2);
    // Calculate label position at bisector, 70% of radius (INSIDE the arc)
    const midAngle = angle1 + angleDiff / 2;
    const labelRadius = radius * 0.7;
    return (
      <g>
        <path
          d={`M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} ${sweepFlag} ${x2} ${y2}`}
          stroke={GCSE_STANDARDS.colors.angle}
          strokeWidth="1.5"
          fill="none"
        />
        {label && (
          <text
            x={vx + labelRadius * Math.cos(midAngle)}
            y={vy + labelRadius * Math.sin(midAngle)}
            fontSize="11"
            fill={GCSE_STANDARDS.colors.angle}
            textAnchor="middle"
            dominantBaseline="middle"
          >
            {label}°
          </text>
        )}
      </g>
    );
  };

  const renderSpecialShape = () => {
    switch (type) {
      case "circle": {
        const center = data.center || [0, 0];
        const radius = data.radius || 5;
        const cx = scaleX(center[0]);
        const cy = scaleY(center[1]);
        const r = (radius / (maxX - minX || 10)) * (svgWidth - 2 * svgPadding);
        if (Number.isNaN(cx) || Number.isNaN(cy) || Number.isNaN(r) || r <= 0)
          return null;
        return (
          <g>
            <circle
              cx={cx}
              cy={cy}
              r={r}
              fill={style.fill || "none"}
              stroke={style.stroke || GCSE_STANDARDS.colors.primary}
              strokeWidth={style.strokeWidth || "2"}
            />
            {data.showCenter && (
              <circle cx={cx} cy={cy} r="3" fill={GCSE_STANDARDS.colors.primary} />
            )}
            {data.showRadius && (
              <line
                x1={cx}
                y1={cy}
                x2={scaleX(center[0] + radius)}
                y2={cy}
                stroke={GCSE_STANDARDS.colors.primary}
                strokeWidth="2"
                strokeDasharray="5,5"
              />
            )}
          </g>
        );
      }
      case "sector": {
        const sectorCenter = data.center || [0, 0];
        const sectorRadius = data.radius || 5;
        const startAngle = ((data.startAngle || 0) * Math.PI) / 180;
        const endAngle = ((data.endAngle || 90) * Math.PI) / 180;

        const x1 = scaleX(sectorCenter[0] + sectorRadius * Math.cos(startAngle));
        const y1 = scaleY(sectorCenter[1] + sectorRadius * Math.sin(startAngle));
        const x2 = scaleX(sectorCenter[0] + sectorRadius * Math.cos(endAngle));
        const y2 = scaleY(sectorCenter[1] + sectorRadius * Math.sin(endAngle));

        const largeArcFlag = Math.abs(endAngle - startAngle) > Math.PI ? 1 : 0;
        const R = (sectorRadius / (maxX - minX || 10)) * (svgWidth - 2 * svgPadding);

        return (
          <path
            d={`M ${scaleX(sectorCenter[0])} ${scaleY(
              sectorCenter[1]
            )} L ${x1} ${y1} A ${R} ${R} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`}
            fill={style.fill || GCSE_STANDARDS.colors.fill}
            stroke={style.stroke || GCSE_STANDARDS.colors.primary}
            strokeWidth={style.strokeWidth || "2"}
          />
        );
      }
      default:
        return null;
    }
  };

  return (
    <div className="geometry-container flex justify-center my-4">
      <svg
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        className="w-full h-auto max-w-md border border-gray-200 rounded-lg bg-white shadow-sm"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Geometry"
      >
        {(type === "circle" || type === "sector") && renderSpecialShape()}

        {vertices.length > 0 && (
          <>
            {showDiagonals && vertices.length > 3 && (
              <g className="diagonals">
                {vertices.map((v1, i) =>
                  vertices.slice(i + 2).map((v2, j) => {
                    if (i === 0 && i + j + 2 === vertices.length - 1) return null;
                    if (
                      !v1 || !v2 || !Array.isArray(v1) || !Array.isArray(v2) ||
                      v1.length < 2 || v2.length < 2
                    )
                      return null;
                    const x1 = scaleX(v1[0]);
                    const y1 = scaleY(v1[1]);
                    const x2 = scaleX(v2[0]);
                    const y2 = scaleY(v2[1]);
                    if (
                      Number.isNaN(x1) || Number.isNaN(y1) ||
                      Number.isNaN(x2) || Number.isNaN(y2)
                    )
                      return null;
                    return (
                      <line
                        key={`diagonal-${i}-${i + j + 2}`}
                        x1={x1}
                        y1={y1}
                        x2={x2}
                        y2={y2}
                        stroke={GCSE_STANDARDS.colors.secondary}
                        strokeWidth="1"
                        strokeDasharray="5,5"
                      />
                    );
                  })
                )}
              </g>
            )}

            <polygon
              points={vertices
                .filter((v) => v && Array.isArray(v) && v.length >= 2)
                .map((v) => {
                  const x = scaleX(v[0]);
                  const y = scaleY(v[1]);
                  return Number.isNaN(x) || Number.isNaN(y) ? null : `${x},${y}`;
                })
                .filter(Boolean)
                .join(" ")}
              fill={style.fill || GCSE_STANDARDS.colors.fill}
              stroke={style.stroke || GCSE_STANDARDS.colors.primary}
              strokeWidth={style.strokeWidth || "2"}
            />

            {showAngles && angles.length > 0 && (
              <g className="angles">
                {angles.map((angle, i) => {
                  const vertex = vertices[i];
                  const prev = vertices[i === 0 ? vertices.length - 1 : i - 1];
                  const next = vertices[(i + 1) % vertices.length];
                  if (
                    !vertex || !prev || !next ||
                    !Array.isArray(vertex) || !Array.isArray(prev) || !Array.isArray(next) ||
                    vertex.length < 2 || prev.length < 2 || next.length < 2
                  )
                    return null;
                  return (
                    <g key={`angle-${i}`}>
                      {drawAngleArc(vertex, prev, next, angle, angle)}
                    </g>
                  );
                })}
              </g>
            )}

            {showMeasurements && sides.length > 0 && (
              <g className="measurements">
                {sides.map((length, i) => {
                  const v1 = vertices[i];
                  const v2 = vertices[(i + 1) % vertices.length];
                  
                  const x1 = scaleX(v1[0]);
                  const y1 = scaleY(v1[1]);
                  const x2 = scaleX(v2[0]);
                  const y2 = scaleY(v2[1]);
                  
                  const labelData = calculateLineLabel(
                    x1, y1, x2, y2, 
                    length, 
                    'above'
                  );

                  return (
                    <text
                      key={`side-${i}`}
                      x={labelData.labelX}
                      y={labelData.labelY}
                      fontSize={GCSE_STANDARDS.fontSize.default}
                      fill={GCSE_STANDARDS.colors.primary}
                      textAnchor="middle"
                      dominantBaseline="middle"
                    >
                      {length}
                    </text>
                  );
                })}
              </g>
            )}

            {labels.length > 0 && (
              <g className="labels">
                {vertices.map((v, i) => {
                  if (!v || !Array.isArray(v) || v.length < 2) return null;
                  const cx = scaleX(v[0]);
                  const cy = scaleY(v[1]);
                  if (Number.isNaN(cx) || Number.isNaN(cy)) return null;
                  
                  // Use custom label position if provided, otherwise auto-position
                  const labelQuadrant = labelPositions[i] || 'topRight';
                  const labelPos = calculateLabelPosition(cx, cy, labelQuadrant);
                  
                  return (
                    <g key={`vertex-${i}`}>
                      <circle cx={cx} cy={cy} r="3" fill={GCSE_STANDARDS.colors.primary} />
                      {labels[i] && (
                        <text
                          x={labelPos.x}
                          y={labelPos.y}
                          fontSize={GCSE_STANDARDS.fontSize.default}
                          fontWeight="normal"
                          fill={GCSE_STANDARDS.colors.primary}
                          textAnchor="middle"
                          dominantBaseline="middle"
                        >
                          {labels[i]}
                        </text>
                      )}
                    </g>
                  );
                })}
              </g>
            )}

            {transformations.length > 0 &&
              transformations.map((transform, i) => {
                let transformedVertices = [...vertices];

                switch (transform.type) {
                  case "translate":
                    transformedVertices = vertices.map((v) => [
                      v[0] + transform.dx,
                      v[1] + transform.dy,
                    ]);
                    break;
                  case "rotate": {
                    const ang = (transform.angle * Math.PI) / 180;
                    const cx = transform.center?.[0] ?? 0;
                    const cy = transform.center?.[1] ?? 0;
                    transformedVertices = vertices.map((v) => {
                      const x = v[0] - cx;
                      const y = v[1] - cy;
                      return [
                        x * Math.cos(ang) - y * Math.sin(ang) + cx,
                        x * Math.sin(ang) + y * Math.cos(ang) + cy,
                      ];
                    });
                    break;
                  }
                  case "reflect":
                    if (transform.axis === "x") {
                      transformedVertices = vertices.map((v) => [v[0], -v[1]]);
                    } else if (transform.axis === "y") {
                      transformedVertices = vertices.map((v) => [-v[0], v[1]]);
                    }
                    break;
                  case "scale":
                    transformedVertices = vertices.map((v) => [
                      v[0] * transform.factor,
                      v[1] * transform.factor,
                    ]);
                    break;
                  default:
                    break;
                }

                return (
                  <polygon
                    key={`transform-${i}`}
                    points={transformedVertices
                      .map((v) => `${scaleX(v[0])},${scaleY(v[1])}`)
                      .join(" ")}
                    fill="none"
                    stroke={transform.color || GCSE_STANDARDS.colors.point}
                    strokeWidth="2"
                    strokeDasharray="5,5"
                  />
                );
              })}
          </>
        )}
      </svg>
    </div>
  );
};

const MemoGeometryRenderer = memo(GeometryRenderer);

// =====================================================================
// NUMBER LINE RENDERER
// =====================================================================

const NumberLineRenderer = ({ data = {} }) => {
  const {
    min = -10,
    max = 10,
    step = 1,
    points = [],
    intervals = [],
    inequalities = [],
    labels = true,
    width: svgWidth = 600,
    height: svgHeight = 100,
    padding: svgPadding = 50,
  } = data;

  const lineY = svgHeight / 2;

  const scale = useCallback(
    (value) =>
      svgPadding +
      ((value - min) / (max - min)) * (svgWidth - 2 * svgPadding),
    [min, max, svgWidth, svgPadding]
  );

  const ticks = useMemo(
    () =>
      Array.from(
        { length: Math.floor((max - min) / step) + 1 },
        (_, i) => min + i * step
      ),
    [min, max, step]
  );

  return (
    <div className="number-line-container flex justify-center my-4">
      <svg
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        className="w-full h-auto max-w-lg"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Number line"
      >
        <line
          x1={svgPadding}
          y1={lineY}
          x2={svgWidth - svgPadding}
          y2={lineY}
          stroke={GCSE_STANDARDS.colors.primary}
          strokeWidth="2"
        />

        <polygon
          points={`${svgWidth - svgPadding},${lineY} ${svgWidth - svgPadding - 8},${
            lineY - 4
          } ${svgWidth - svgPadding - 8},${lineY + 4}`}
          fill={GCSE_STANDARDS.colors.primary}
        />
        <polygon
          points={`${svgPadding},${lineY} ${svgPadding + 8},${lineY - 4} ${
            svgPadding + 8
          },${lineY + 4}`}
          fill={GCSE_STANDARDS.colors.primary}
        />

        {ticks.map((value, i) => {
          const x = scale(value);
          return (
            <g key={`tick-${i}`}>
              <line
                x1={x}
                y1={lineY - 8}
                x2={x}
                y2={lineY + 8}
                stroke={GCSE_STANDARDS.colors.primary}
                strokeWidth="1"
              />
              {labels && (
                <text
                  x={x}
                  y={lineY + 25}
                  fontSize={GCSE_STANDARDS.fontSize.tickLabel}
                  textAnchor="middle"
                  fill={GCSE_STANDARDS.colors.primary}
                >
                  {value}
                </text>
              )}
            </g>
          );
        })}

        {intervals.map((interval, i) => (
          <line
            key={`interval-${i}`}
            x1={scale(interval.start)}
            y1={lineY}
            x2={scale(interval.end)}
            y2={lineY}
            stroke={interval.color || GCSE_STANDARDS.colors.function}
            strokeWidth="6"
            strokeLinecap="round"
          />
        ))}

        {inequalities.map((ineq, i) => {
          const { value, type, direction = "both", color = GCSE_STANDARDS.colors.angle } = ineq;
          const x = scale(value);
          const arrowY = lineY - 20; // Position 20px above the line
          const arrowLength = 20; // Match this with your arrowhead length

          return (
            <g key={`inequality-${i}`}>
              {/* Shaded region ABOVE the line - extends to arrowhead base */}
              {direction === "left" || direction === "both" ? (
                <line
                  x1={svgPadding + arrowLength}
                  y1={arrowY}
                  x2={x}
                  y2={arrowY}
                  stroke={color}
                  strokeWidth="4"
                  opacity="0.6"
                />
              ) : null}
              {direction === "right" || direction === "both" ? (
                <line
                  x1={x}
                  y1={arrowY}
                  x2={svgWidth - svgPadding - arrowLength}
                  y2={arrowY}
                  stroke={color}
                  strokeWidth="4"
                  opacity="0.6"
                />
              ) : null}

              {/* Circle above the line */}
              <circle
                cx={x}
                cy={arrowY}
                r="6"
                fill={type === "closed" ? color : "white"}
                stroke={color}
                strokeWidth="2"
              />

              {/* Arrow pointing RIGHT (if direction is right) */}
              {direction === "right" && (
                <polygon
                  points={`${svgWidth - svgPadding},${arrowY} ${svgWidth - svgPadding - 20},${arrowY - 8} ${svgWidth - svgPadding - 20},${arrowY + 8}`}
                  fill={color}
                />
              )}

              {/* Arrow pointing LEFT (if direction is left) */}
              {direction === "left" && (
                <polygon
                  points={`${svgPadding},${arrowY} ${svgPadding + 20},${arrowY - 8} ${svgPadding + 20},${arrowY + 8}`}
                  fill={color}
                />
              )}
            </g>
          );
        })}

        {points.map((point, i) => (
          <g key={`point-${i}`}>
            <circle
              cx={scale(point.value)}
              cy={lineY}
              r="5"
              fill={point.filled !== false 
                ? (point.color || GCSE_STANDARDS.colors.point)
                : "white"
              }
              stroke={point.color || GCSE_STANDARDS.colors.point}
              strokeWidth="2"
            />
            {point.label && (
              <text
                x={scale(point.value)}
                y={lineY - 15}
                fontSize={GCSE_STANDARDS.fontSize.small}
                textAnchor="middle"
                fill={GCSE_STANDARDS.colors.primary}
              >
                {point.label}
              </text>
            )}
          </g>
        ))}
      </svg>
    </div>
  );
};

const MemoNumberLineRenderer = memo(NumberLineRenderer);

// =====================================================================
// TABLE RENDERER
// =====================================================================

const TableRenderer = ({ data = {} }) => {
  const { headers = [], rows = [], caption = "", showBorder = true } = data;

  return (
    <div className="table-container my-4 overflow-x-auto">
      {caption && (
        <p className="text-center text-sm text-gray-600 mb-2">{caption}</p>
      )}
      <table
        className={`mx-auto ${
          showBorder ? "border-collapse border border-gray-300" : ""
        }`}
      >
        {headers.length > 0 && (
          <thead>
            <tr>
              {headers.map((header, i) => (
                <th
                  key={i}
                  className={`px-4 py-2 bg-gray-100 font-semibold text-left ${
                    showBorder ? "border border-gray-300" : ""
                  }`}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td
                  key={j}
                  className={`px-4 py-2 ${
                    showBorder ? "border border-gray-300" : ""
                  }`}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const MemoTableRenderer = memo(TableRenderer);

// =====================================================================
// DIAGRAM ROUTER
// =====================================================================

const DiagramRenderer = ({ data = {} }) => {
  const { type, ...props } = data;

  switch (type) {
    case "venn":
      return <MemoVennDiagram {...props} />;
    case "tree":
      return <MemoTreeDiagram {...props} />;
    case "flowchart":
      return <MemoFlowChart {...props} />;
    case "bar":
      return <MemoBarChart {...props} />;
    case "pie":
      return <MemoPieChart {...props} />;
    case "histogram":
      return <MemoHistogram {...props} />;
    case "boxplot":
      return <MemoBoxPlot {...props} />;
    default:
      return null;
  }
};

const MemoDiagramRenderer = memo(DiagramRenderer);

// =====================================================================
// STATISTICAL DIAGRAMS
// =====================================================================

const VennDiagram = ({ sets = [], intersections = [], labels = [] }) => {
  const width = 400;
  const height = 300;

  return (
    <div className="venn-diagram-container flex justify-center my-4">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-auto max-w-md"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Venn diagram"
      >
        {sets.length === 2 && (
          <>
            <circle
              cx={width * 0.35}
              cy={height * 0.5}
              r="80"
              fill="rgba(59, 130, 246, 0.3)"
              stroke={GCSE_STANDARDS.colors.function}
              strokeWidth="2"
            />
            <circle
              cx={width * 0.65}
              cy={height * 0.5}
              r="80"
              fill="rgba(239, 68, 68, 0.3)"
              stroke={GCSE_STANDARDS.colors.point}
              strokeWidth="2"
            />
            <text x={width * 0.25} y={height * 0.5} textAnchor="middle" fontSize="14">
              {labels[0] || "A"}
            </text>
            <text x={width * 0.75} y={height * 0.5} textAnchor="middle" fontSize="14">
              {labels[1] || "B"}
            </text>
            <text x={width * 0.5} y={height * 0.5} textAnchor="middle" fontSize="14">
              {intersections[0] || ""}
            </text>
          </>
        )}

        {sets.length === 3 && (
          <>
            <circle
              cx={width * 0.4}
              cy={height * 0.35}
              r="70"
              fill="rgba(59, 130, 246, 0.3)"
              stroke={GCSE_STANDARDS.colors.function}
              strokeWidth="2"
            />
            <circle
              cx={width * 0.6}
              cy={height * 0.35}
              r="70"
              fill="rgba(239, 68, 68, 0.3)"
              stroke={GCSE_STANDARDS.colors.point}
              strokeWidth="2"
            />
            <circle
              cx={width * 0.5}
              cy={height * 0.55}
              r="70"
              fill="rgba(16, 185, 129, 0.3)"
              stroke={GCSE_STANDARDS.colors.angle}
              strokeWidth="2"
            />
          </>
        )}
      </svg>
    </div>
  );
};

const MemoVennDiagram = memo(VennDiagram);

const TreeDiagram = ({ nodes = [], edges = [] }) => {
  const width = 500;
  const height = 400;

  return (
    <div className="tree-diagram-container flex justify-center my-4">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-auto max-w-lg"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Tree diagram"
      >
        {edges.map((edge, i) => (
          <line
            key={`edge-${i}`}
            x1={edge.from.x * width}
            y1={edge.from.y * height}
            x2={edge.to.x * width}
            y2={edge.to.y * height}
            stroke={GCSE_STANDARDS.colors.primary}
            strokeWidth="2"
          />
        ))}

        {nodes.map((node, i) => (
          <g key={`node-${i}`}>
            <circle
              cx={node.x * width}
              cy={node.y * height}
              r="20"
              fill="white"
              stroke={GCSE_STANDARDS.colors.primary}
              strokeWidth="2"
            />
            <text
              x={node.x * width}
              y={node.y * height + 5}
              textAnchor="middle"
              fontSize="14"
            >
              {node.label}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
};

const MemoTreeDiagram = memo(TreeDiagram);

const BarChart = ({ data = [], labels = [], xLabel = "", yLabel = "", title = "" }) => {
  const width = 500;
  const height = 300;
  const padding = 40;
  const barWidth = data.length ? (width - 2 * padding) / data.length - 10 : 0;
  const maxValue = data.length ? Math.max(...data) : 1;

  return (
    <div className="bar-chart-container flex justify-center my-4">
      <svg
        viewBox={`0 0 ${width} ${height + 40}`}
        className="w-full h-auto max-w-lg"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Bar chart"
      >
        {title && (
          <text x={width / 2} y={20} textAnchor="middle" fontSize="16" fontWeight="bold">
            {title}
          </text>
        )}

        <line 
          x1={padding} 
          y1={padding} 
          x2={padding} 
          y2={height - padding} 
          stroke={GCSE_STANDARDS.colors.primary} 
          strokeWidth="2" 
        />
        <line 
          x1={padding} 
          y1={height - padding} 
          x2={width - padding} 
          y2={height - padding} 
          stroke={GCSE_STANDARDS.colors.primary} 
          strokeWidth="2" 
        />

        {data.map((value, i) => {
          const barHeight = (value / (maxValue || 1)) * (height - 2 * padding);
          const x = padding + i * (barWidth + 10) + 10;
          const y = height - padding - barHeight;

          return (
            <g key={`bar-${i}`}>
              <rect 
                x={x} 
                y={y} 
                width={barWidth} 
                height={barHeight} 
                fill={GCSE_STANDARDS.colors.function} 
              />
              <text 
                x={x + barWidth / 2} 
                y={height - padding + 20} 
                textAnchor="middle" 
                fontSize="12"
              >
                {labels[i] || i}
              </text>
              <text 
                x={x + barWidth / 2} 
                y={y - 5} 
                textAnchor="middle" 
                fontSize="10" 
                fill={GCSE_STANDARDS.colors.primary}
              >
                {value}
              </text>
            </g>
          );
        })}

        {xLabel && (
          <text x={width / 2} y={height + 10} textAnchor="middle" fontSize="14">
            {xLabel}
          </text>
        )}
        {yLabel && (
          <text 
            x={15} 
            y={height / 2} 
            textAnchor="middle" 
            fontSize="14" 
            transform={`rotate(-90 15 ${height / 2})`}
          >
            {yLabel}
          </text>
        )}
      </svg>
    </div>
  );
};

const MemoBarChart = memo(BarChart);

const PieChart = ({ data = [], labels = [], colors = [], title = "" }) => {
  const width = 400;
  const height = 400;
  const centerX = width / 2;
  const centerY = height / 2;
  const radius = Math.min(width, height) / 2 - 40;

  const total = data.reduce((sum, v) => sum + v, 0) || 1;
  const defaultColors = [
    GCSE_STANDARDS.colors.function,
    GCSE_STANDARDS.colors.point,
    GCSE_STANDARDS.colors.angle,
    "#f59e0b",
    GCSE_STANDARDS.colors.shape,
    "#ec4899"
  ];

  let cumulativeAngle = 0;

  return (
    <div className="pie-chart-container flex justify-center my-4">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-auto max-w-md"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Pie chart"
      >
        {title && (
          <text x={centerX} y={30} textAnchor="middle" fontSize="16" fontWeight="bold">
            {title}
          </text>
        )}

        {data.map((value, i) => {
          const angle = (value / total) * 2 * Math.PI;
          const startAngle = cumulativeAngle;
          const endAngle = cumulativeAngle + angle;

          const x1 = centerX + radius * Math.cos(startAngle);
          const y1 = centerY + radius * Math.sin(startAngle);
          const x2 = centerX + radius * Math.cos(endAngle);
          const y2 = centerY + radius * Math.sin(endAngle);

          const largeArcFlag = angle > Math.PI ? 1 : 0;

          const pathData = [
            `M ${centerX} ${centerY}`,
            `L ${x1} ${y1}`,
            `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
            `Z`,
          ].join(" ");

          const labelAngle = startAngle + angle / 2;
          const labelX = centerX + radius * 0.7 * Math.cos(labelAngle);
          const labelY = centerY + radius * 0.7 * Math.sin(labelAngle);

          cumulativeAngle = endAngle;

          return (
            <g key={`slice-${i}`}>
              <path
                d={pathData}
                fill={colors[i] || defaultColors[i % defaultColors.length]}
                stroke="white"
                strokeWidth="2"
              />
              <text
                x={labelX}
                y={labelY}
                textAnchor="middle"
                fontSize="12"
                fill="white"
                fontWeight="bold"
              >
                {labels[i] || `${Math.round((value / total) * 100)}%`}
              </text>
            </g>
          );
        })}

        {labels.length > 0 && (
          <g>
            {labels.map((label, i) => (
              <g key={`legend-${i}`}>
                <rect
                  x={width - 100}
                  y={50 + i * 25}
                  width="15"
                  height="15"
                  fill={colors[i] || defaultColors[i % defaultColors.length]}
                />
                <text x={width - 80} y={62 + i * 25} fontSize="12">
                  {label}
                </text>
              </g>
            ))}
          </g>
        )}
      </svg>
    </div>
  );
};

const MemoPieChart = memo(PieChart);

const Histogram = ({ data = [], bins = 10, xLabel = "", yLabel = "Frequency", title = "" }) => {
  const width = 500;
  const height = 300;
  const padding = 40;

  if (!data.length) data = [0];

  const min = Math.min(...data);
  const max = Math.max(...data);
  const binWidth = (max - min || 1) / (bins || 1);
  const histogram = Array(Math.max(1, bins)).fill(0);

  data.forEach((value) => {
    const idx = Math.min(Math.floor((value - min) / binWidth), histogram.length - 1);
    histogram[idx]++;
  });

  const maxFreq = Math.max(...histogram) || 1;
  const barWidth = (width - 2 * padding) / histogram.length;

  return (
    <div className="histogram-container flex justify-center my-4">
      <svg
        viewBox={`0 0 ${width} ${height + 40}`}
        className="w-full h-auto max-w-lg"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Histogram"
      >
        {title && (
          <text x={width / 2} y={20} textAnchor="middle" fontSize="16" fontWeight="bold">
            {title}
          </text>
        )}

        <line 
          x1={padding} 
          y1={padding} 
          x2={padding} 
          y2={height - padding} 
          stroke={GCSE_STANDARDS.colors.primary} 
          strokeWidth="2" 
        />
        <line 
          x1={padding} 
          y1={height - padding} 
          x2={width - padding} 
          y2={height - padding} 
          stroke={GCSE_STANDARDS.colors.primary} 
          strokeWidth="2" 
        />

        {histogram.map((freq, i) => {
          const barHeight = (freq / maxFreq) * (height - 2 * padding);
          const x = padding + i * barWidth;
          const y = height - padding - barHeight;

          return (
            <g key={`bar-${i}`}>
              <rect 
                x={x} 
                y={y} 
                width={barWidth} 
                height={barHeight} 
                fill={GCSE_STANDARDS.colors.function} 
                stroke="white" 
                strokeWidth="1" 
              />
              <text 
                x={x + barWidth / 2} 
                y={height - padding + 20} 
                textAnchor="middle" 
                fontSize="10"
              >
                {(min + i * binWidth).toFixed(1)}
              </text>
            </g>
          );
        })}

        {Array.from({ length: 5 }, (_, i) => {
          const value = (Math.max(0, maxFreq) / 4) * i;
          const y = height - padding - (i / 4) * (height - 2 * padding);
          return (
            <g key={`y-label-${i}`}>
              <line 
                x1={padding - 5} 
                y1={y} 
                x2={padding} 
                y2={y} 
                stroke={GCSE_STANDARDS.colors.primary} 
                strokeWidth="1" 
              />
              <text x={padding - 10} y={y + 5} textAnchor="end" fontSize="10">
                {Math.round(value)}
              </text>
            </g>
          );
        })}

        {xLabel && (
          <text x={width / 2} y={height + 20} textAnchor="middle" fontSize="14">
            {xLabel}
          </text>
        )}
        {yLabel && (
          <text 
            x={15} 
            y={height / 2} 
            textAnchor="middle" 
            fontSize="14" 
            transform={`rotate(-90 15 ${height / 2})`}
          >
            {yLabel}
          </text>
        )}
      </svg>
    </div>
  );
};

const MemoHistogram = memo(Histogram);

const BoxPlot = ({ data = [], labels = [], title = "" }) => {
  const width = 500;
  const height = 300;
  const padding = 40;

  const calculateStats = (values) => {
    const sorted = [...values].sort((a, b) => a - b);
    const n = sorted.length || 1;
    const q1 = sorted[Math.floor(n * 0.25)];
    const median = sorted[Math.floor(n * 0.5)];
    const q3 = sorted[Math.floor(n * 0.75)];
    const min = sorted[0];
    const max = sorted[n - 1];
    return { min, q1, median, q3, max };
  };

  const allValues = data.flat().length ? data.flat() : [0, 0];
  const overallMin = Math.min(...allValues);
  const overallMax = Math.max(...allValues);

  const scale = (value) =>
    padding +
    ((value - overallMin) / (overallMax - overallMin || 1)) * (width - 2 * padding);

  return (
    <div className="boxplot-container flex justify-center my-4">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-auto max-w-lg"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Box plot"
      >
        {title && (
          <text x={width / 2} y={20} textAnchor="middle" fontSize="16" fontWeight="bold">
            {title}
          </text>
        )}

        <line 
          x1={padding} 
          y1={height - padding} 
          x2={width - padding} 
          y2={height - padding} 
          stroke={GCSE_STANDARDS.colors.primary} 
          strokeWidth="2" 
        />

        {data.map((dataset, i) => {
          const stats = calculateStats(dataset.length ? dataset : [0, 0, 0]);
          const y = padding + (i + 0.5) * ((height - 2 * padding) / (data.length || 1));
          const boxHeight = 30;

          return (
            <g key={`boxplot-${i}`}>
              <line 
                x1={scale(stats.min)} 
                y1={y} 
                x2={scale(stats.max)} 
                y2={y} 
                stroke={GCSE_STANDARDS.colors.primary} 
                strokeWidth="1" 
              />
              <line 
                x1={scale(stats.min)} 
                y1={y - boxHeight / 4} 
                x2={scale(stats.min)} 
                y2={y + boxHeight / 4} 
                stroke={GCSE_STANDARDS.colors.primary} 
                strokeWidth="2" 
              />
              <line 
                x1={scale(stats.max)} 
                y1={y - boxHeight / 4} 
                x2={scale(stats.max)} 
                y2={y + boxHeight / 4} 
                stroke={GCSE_STANDARDS.colors.primary} 
                strokeWidth="2" 
              />
              <rect
                x={scale(stats.q1)}
                y={y - boxHeight / 2}
                width={Math.max(1, scale(stats.q3) - scale(stats.q1))}
                height={boxHeight}
                fill="rgba(59, 130, 246, 0.5)"
                stroke={GCSE_STANDARDS.colors.function}
                strokeWidth="2"
              />
              <line 
                x1={scale(stats.median)} 
                y1={y - boxHeight / 2} 
                x2={scale(stats.median)} 
                y2={y + boxHeight / 2} 
                stroke={GCSE_STANDARDS.colors.primary} 
                strokeWidth="3" 
              />
              {labels[i] && (
                <text x={padding - 10} y={y + 5} textAnchor="end" fontSize="12">
                  {labels[i]}
                </text>
              )}
            </g>
          );
        })}

        {Array.from({ length: 5 }, (_, i) => {
          const value = overallMin + (i / 4) * (overallMax - overallMin || 1);
          const x = scale(value);
          return (
            <g key={`x-label-${i}`}>
              <line 
                x1={x} 
                y1={height - padding} 
                x2={x} 
                y2={height - padding + 5} 
                stroke={GCSE_STANDARDS.colors.primary} 
                strokeWidth="1" 
              />
              <text x={x} y={height - padding + 20} textAnchor="middle" fontSize="10">
                {Number.isFinite(value) ? value.toFixed(1) : "0.0"}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

const MemoBoxPlot = memo(BoxPlot);

const FlowChart = ({ nodes = [], connections = [] }) => {
  const width = 600;
  const height = 400;
  const arrowId = useId();

  return (
    <div className="flowchart-container flex justify-center my-4">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-auto max-w-2xl"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Flow chart"
      >
        {connections.map((conn, i) => (
          <line
            key={`conn-${i}`}
            x1={conn.from.x * width}
            y1={conn.from.y * height}
            x2={conn.to.x * width}
            y2={conn.to.y * height}
            stroke={GCSE_STANDARDS.colors.primary}
            strokeWidth="2"
            markerEnd={`url(#arrow-${arrowId})`}
          />
        ))}

        {nodes.map((node, i) => {
          const x = node.x * width;
          const y = node.y * height;
          const w = node.width || 100;
          const h = node.height || 50;

          return (
            <g key={`node-${i}`}>
              {node.type === "diamond" ? (
                <polygon
                  points={`${x},${y - h / 2} ${x + w / 2},${y} ${x},${y + h / 2} ${x - w / 2},${y}`}
                  fill="white"
                  stroke={GCSE_STANDARDS.colors.primary}
                  strokeWidth="2"
                />
              ) : (
                <rect
                  x={x - w / 2}
                  y={y - h / 2}
                  width={w}
                  height={h}
                  rx={node.type === "rounded" ? 10 : 0}
                  fill="white"
                  stroke={GCSE_STANDARDS.colors.primary}
                  strokeWidth="2"
                />
              )}
              <text x={x} y={y + 5} textAnchor="middle" fontSize="12">
                {node.label}
              </text>
            </g>
          );
        })}

        <defs>
          <marker
            id={`arrow-${arrowId}`}
            markerWidth="10"
            markerHeight="10"
            refX="9"
            refY="3"
            orient="auto"
          >
            <polygon points="0 0, 10 3, 0 6" fill={GCSE_STANDARDS.colors.primary} />
          </marker>
        </defs>
      </svg>
    </div>
  );
};

const MemoFlowChart = memo(FlowChart);

export default RenderBlocks;
export { GCSE_STANDARDS };